import type {
  OrganizationSampleDataOutcome,
  OrganizationSampleDataStatus,
  RemoveSampleDataResult,
  SampleDataCallsStatus,
  SampleDataCounts,
  SampleDataSkippedItem,
} from '../types/sampleData';

/*
 * The API and web deploy separately, so every field is read defensively: a missing array becomes
 * empty, a missing count becomes 0, and an unknown enum value becomes null rather than a crash.
 */

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asRecord = (value: unknown): UnknownRecord => (isRecord(value) ? value : {});

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

const strOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

const CALLS_STATUSES: readonly SampleDataCallsStatus[] = [
  'NotApplicable',
  'Pending',
  'Seeding',
  'Seeded',
  'Failed',
];

const OUTCOMES: readonly OrganizationSampleDataOutcome[] = ['seeded', 'failed', 'skipped'];

const toCallsStatus = (value: unknown): SampleDataCallsStatus | null =>
  CALLS_STATUSES.find((s) => s === value) ?? null;

const toCounts = (value: unknown): SampleDataCounts => {
  const raw = asRecord(value);
  return {
    teams: count(raw.teams),
    prospects: count(raw.prospects),
    rosterBoards: count(raw.rosterBoards),
    rosterBoardPlayers: count(raw.rosterBoardPlayers),
    notes: count(raw.notes),
    events: count(raw.events),
    calls: count(raw.calls),
  };
};

export const normalizeSkippedItems = (value: unknown): SampleDataSkippedItem[] =>
  (Array.isArray(value) ? value.filter(isRecord) : []).map((raw) => ({
    entityType: str(raw.entityType),
    label: str(raw.label),
    reason: str(raw.reason),
  }));

export const normalizeSampleDataStatus = (value: unknown): OrganizationSampleDataStatus => {
  const raw = asRecord(value);
  const preview = asRecord(raw.removalPreview);
  return {
    active: raw.active === true,
    seededAt: strOrNull(raw.seededAt),
    counts: toCounts(raw.counts),
    callsStatus: toCallsStatus(raw.callsStatus),
    callsError: strOrNull(raw.callsError),
    removalPreview: {
      removable: count(preview.removable),
      skipped: normalizeSkippedItems(preview.skipped),
    },
  };
};

export const normalizeRemoveSampleDataResult = (value: unknown): RemoveSampleDataResult => {
  const raw = asRecord(value);
  return {
    removed: count(raw.removed),
    skipped: normalizeSkippedItems(raw.skipped),
    status: isRecord(raw.status) ? normalizeSampleDataStatus(raw.status) : null,
  };
};

/** Reads `sampleData` off a create-organization response. Anything unrecognised is undefined. */
export const toSampleDataOutcome = (value: unknown): OrganizationSampleDataOutcome | undefined =>
  OUTCOMES.find((o) => o === value);
